import { type ReactNode, useCallback, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { createTRPCClient, httpBatchLink } from "@trpc/client";
import { createTRPCReact } from "@trpc/react-query";
import { DateTime } from "luxon";

import { type CaseIQPayload, type CaseIQResponse } from "../../../Components/Ava2/types";
import { useAuthContext } from "../../../Context/AuthContext";
import { type FirebaseUserWithEmail } from "../../../Context/UserContext";
import type { CaseIQRouter } from "../../../../../services/chatgpt/case-iq/src/router";

const minWords = 4;
let caseIqSessionId = "";
let zdTicketId = "";

export const setCaseIqSessionId = (sessionId: string) => {
  caseIqSessionId = sessionId;
};
export const setZdTicketId = (ticketId: string) => {
  zdTicketId = ticketId;
};

const caseIQBaseUrlV1trpc = "/api/caseiq/v1/trpc";

export const caseIQTrpc = createTRPCReact<CaseIQRouter>();

export const CaseIQClientProvider = ({ children }: { children: ReactNode }) => {
  const { currentUser } = useAuthContext();

  const getToken = useCallback(
    async (forceRefresh?: boolean) => currentUser?.getIdTokenResult(forceRefresh),
    [currentUser]
  );
  const queryClient = useQueryClient();
  const [trpcClient] = useState(() =>
    caseIQTrpc.createClient({
      links: [
        httpBatchLink({
          url: `${window.location.origin}${caseIQBaseUrlV1trpc}`,
          async headers() {
            const currentToken = await getToken();
            return {
              Authorization: currentToken ? `Bearer ${currentToken.token}` : undefined,
              "x-caseiq-session-id": caseIqSessionId,
              "x-zendesk-ticket-id": zdTicketId,
              "x-caseiq-frontend-timestamp": DateTime.now().toUTC().toISO(),
            };
          },
        }),
      ],
    })
  );

  return (
    <caseIQTrpc.Provider client={trpcClient} queryClient={queryClient}>
      {children}
    </caseIQTrpc.Provider>
  );
};

export function getClient(currentUser: FirebaseUserWithEmail | null | undefined) {
  return createTRPCClient<CaseIQRouter>({
    links: [
      httpBatchLink({
        url: `${window.location.origin}${caseIQBaseUrlV1trpc}`,
        async headers() {
          const currentToken = await currentUser?.getIdTokenResult();
          return {
            Authorization: currentToken ? `Bearer ${currentToken.token}` : undefined,
            "x-caseiq-session-id": caseIqSessionId,
            "x-zendesk-ticket-id": zdTicketId,
            "x-caseiq-frontend-timestamp": DateTime.now().toUTC().toISO(),
          };
        },
      }),
    ],
  });
}

export const stripHtmlTags = (html: string): string => html.replace(/<\/?[^>]+(>|$)/g, "");

const caseIQIsEmpty = (caseIQpayload: CaseIQPayload) => !caseIQpayload.description;

export const useCaseIQByDetails = () => {
  const [loading, setLoading] = useState(false);

  const { mutateAsync } = caseIQTrpc.v2.ticketDetailsInference.useMutation();

  const fetchCaseIQ = useCallback(
    async (caseIQpayload: CaseIQPayload) => {
      const hasEnoughWords = caseIQpayload.description ? caseIQpayload.description.split(" ").length >= minWords : true;

      if (hasEnoughWords && !caseIQIsEmpty(caseIQpayload)) {
        setLoading(true);
        const data = await mutateAsync(caseIQpayload);
        setLoading(false);
        return data;
      } else {
        return null;
      }
    },
    [mutateAsync]
  );

  return { loading, fetchCaseIQ };
};

export const useCaseIQForAddTicketId = () => {
  const { mutateAsync } = caseIQTrpc.v2.ticketDetailsInference.useMutation();

  return mutateAsync;
};

export const useCaseIQByID = () => {
  const { mutateAsync } = caseIQTrpc.v2.ticketDetailsInference.useMutation();

  const fetchCaseIQ = async (conversationId: string) => {
    const data: CaseIQResponse = await mutateAsync({ conversationId });
    return data;
  };

  return fetchCaseIQ;
};
